import api from '@/api';
import type { AiQuotaKey, BillingResourceKey } from '@/api/billing';

export interface UsageBucket {
  used: number;
  max: number;
}

export interface UsageDashboardMetrics {
  trovesAddedThisWeek: number;
  tasksDueWithin7Days: number;
  recentNoteTitle?: string;
}

export interface UsageSnapshot {
  planId?: number;
  effectivePlanId?: number;
  readOnlyMode?: boolean;
  status?: string;
  resources: Partial<Record<BillingResourceKey, UsageBucket>>;
  ai?: Partial<Record<AiQuotaKey, UsageBucket>>;
  dashboard?: UsageDashboardMetrics;
  /** Per-note HTML body byte cap for the active plan. */
  maxNoteBodyBytes?: number;
}

export type UsageView = 'dashboard' | 'quota' | 'full';

const CACHE_TTL_MS: Record<UsageView, number> = {
  dashboard: 5 * 60_000,
  quota: 5 * 60_000,
  full: 60_000,
};

type CacheEntry = { cachedAt: number; snapshot: UsageSnapshot };
const cacheByView = new Map<UsageView, CacheEntry>();
const inFlightByView = new Map<UsageView, Promise<UsageSnapshot>>();

/** Clears client usage cache (tests). */
export function clearUsageSnapshotCache(view?: UsageView): void {
  if (view) {
    cacheByView.delete(view);
    inFlightByView.delete(view);
    return;
  }
  cacheByView.clear();
  inFlightByView.clear();
}

export type GetUsageSnapshotOptions = {
  view?: UsageView;
};

export async function getUsageSnapshot(
  force = false,
  options: GetUsageSnapshotOptions = {}
): Promise<UsageSnapshot> {
  const view = options.view ?? 'full';
  const ttl = CACHE_TTL_MS[view];
  const hit = cacheByView.get(view);
  if (!force && hit && Date.now() - hit.cachedAt < ttl) {
    return hit.snapshot;
  }
  if (!force) {
    const pending = inFlightByView.get(view);
    if (pending) return pending;
  }

  const promise = (async () => {
    const { data } = await api.get<{ status: string; usage?: UsageSnapshot }>('/usage', {
      params: { view, ...(force ? { force: 'true' } : {}) },
    });
    if (data.status !== 'success' || !data.usage) throw new Error('Failed to load usage');
    cacheByView.set(view, { cachedAt: Date.now(), snapshot: data.usage });
    return data.usage;
  })();

  inFlightByView.set(view, promise);
  try {
    return await promise;
  } finally {
    if (inFlightByView.get(view) === promise) inFlightByView.delete(view);
  }
}
